/**
 * Whether the API is reachable. A request that gets no answer, or a 502,
 * 503 or 504, makes it *suspect*, not unreachable (2026-11-04: "the lost
 * connection screen appears on occasion while browsing"): one slow query
 * past its time limit (503), a request cut off by a page change, or a blip
 * is not the API being down. So `/api/health/live` is asked at once, and
 * again a moment later; only when both fail does the shell show its "back
 * soon" state, and then it checks with growing pauses (2 s up to 30 s, and
 * at once when the browser comes back online) until it answers. Offline,
 * it is unreachable at once. The failed request still fails, with its own
 * message.
 *
 * Nothing is probed while requests succeed, so pages that never call the
 * API (the public home page) never load the client or the health contract.
 */
import { useSyncExternalStore } from 'react';

let reachable = true;
let timer: ReturnType<typeof setTimeout> | undefined;
let attempt = 0;
const listeners = new Set<() => void>();

const LIVE = '/api/health/live';
const MAX_PAUSE_MS = 30_000;

function set(next: boolean) {
  if (reachable === next) return;
  reachable = next;
  for (const listener of listeners) listener();
}

/** Asks `/api/health/live` once; true when it answers `ok`. */
export async function probe(fetchFn: typeof fetch = fetch): Promise<boolean> {
  try {
    const response = await fetchFn(LIVE, {
      headers: { accept: 'application/json' },
      cache: 'no-store',
    });
    const type = response.headers.get('content-type') ?? '';
    if (!response.ok || !type.includes('application/json')) return false;
    const [{ apiStateFrom }, body] = await Promise.all([
      import('./health.js'),
      response.json() as Promise<unknown>,
    ]);
    return apiStateFrom(body) === 'online';
  } catch {
    return false;
  }
}

function schedule() {
  if (timer !== undefined) return;
  const pause = Math.min(MAX_PAUSE_MS, 2000 * 2 ** attempt);
  attempt += 1;
  timer = setTimeout(() => {
    timer = undefined;
    void recheck();
  }, pause);
}

/** Checks now; on failure, schedules the next check. */
export async function recheck(): Promise<boolean> {
  const ok = await probe();
  if (ok) {
    attempt = 0;
    set(true);
  } else {
    set(false);
    schedule();
  }
  return ok;
}

/** The pause before asking the health check a second time. */
const CONFIRM_MS = 1500;

/** A check started by a suspect request, while it runs. */
let confirming: Promise<void> | undefined;

/**
 * Asks the health check now and, if it fails, once more a moment later:
 * only two failures in a row mean the API is down.
 */
async function confirm(): Promise<void> {
  if (await probe()) return;
  await new Promise((resolve) => setTimeout(resolve, CONFIRM_MS));
  if (await probe()) return;
  set(false);
  schedule();
}

/**
 * Records that a request found the API unavailable. Offline, or already
 * known to be down, that is final; otherwise the health check decides.
 */
export function markUnreachable(): void {
  // Offline only when the browser says so (`onLine` is missing outside browsers).
  const offline = typeof navigator !== 'undefined' && 'onLine' in navigator && !navigator.onLine;
  if (!reachable || offline) {
    set(false);
    schedule();
    return;
  }
  confirming ??= confirm().finally(() => {
    confirming = undefined;
  });
}

/** Resolves once any check a suspect request started has decided (for tests). */
export function connectivitySettled(): Promise<void> {
  return confirming ?? Promise.resolve();
}

/** Whether the API answered most recently. */
export function isApiReachable(): boolean {
  return reachable;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Re-renders when the API becomes reachable or unreachable. */
export function useApiReachable(): boolean {
  return useSyncExternalStore(subscribe, isApiReachable, () => true);
}

/**
 * Starts watching: while the API is unreachable, the browser coming back
 * online triggers a check at once.
 *
 * @returns Stops watching.
 */
export function startConnectivityWatch(): () => void {
  const online = () => {
    if (reachable) return;
    if (timer !== undefined) clearTimeout(timer);
    timer = undefined;
    attempt = 0;
    void recheck();
  };
  window.addEventListener('online', online);
  return () => {
    window.removeEventListener('online', online);
    if (timer !== undefined) clearTimeout(timer);
    timer = undefined;
  };
}
